import React, { useState, useEffect, useRef } from 'react';  // Add useEffect to the imports
import { 
  Sport, 
  Location, 
  GameProposal, 
  PadelCategory, 
  BeachTennisCategory, 
  Player, 
  GameGender, 
  TennisCategory,
  Group, // Add Group import
} from '../types';
import { generateTimeOptions } from '../utils/timeUtils';
import { toLocalISOString, normalizeDate } from '../utils/dateUtils';
import { validateTimes, formatTimeForSelect } from '../utils/formValidation';
import CategorySelector from './game/CategorySelector';
import LocationSelector from './game/LocationSelector';
import LocationInfoTooltip from './tooltips/LocationInfoTooltip';
import CategoryTooltip from './tooltips/CategoryTooltip';
import VisibilityTooltip from './tooltips/VisibilityTooltip';
import { X, Search, Plus, Camera } from 'lucide-react';  // Add this import
import { supabase } from '../lib/supabase';
import { resizeImage } from '../utils/imageUtils';
import { GiTennisBall } from 'react-icons/gi';


const padelCategories: PadelCategory[] = ['CAT 1', 'CAT 2', 'CAT 3', 'CAT 4', 'CAT 5', 'CAT 6'];
const beachTennisCategories: BeachTennisCategory[] = [
  'INICIANTE',
  'CAT C',
  'CAT B',
  'CAT A',
  'PROFISSIONAL'
];
const tennisCategories: TennisCategory[] = [
  '1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0'
];

interface GameProposalFormProps {
  onSubmit: (data: {
    sport: Sport;
    locations: string[];
    date: string;
    startTime: string;
    endTime: string;
    description: string;
    requiredCategories: (PadelCategory | BeachTennisCategory | TennisCategory)[];
    gender: GameGender;
    maxPlayers: number;
    is_public: boolean;
    groups?: string[];
    is_organizer_only: boolean; // Add this line
    extra_cost: string;
  }) => void;
  onClose: () => void;
  initialData?: Partial<GameProposal>;
  isEditing?: boolean;
  currentUser: Player | null;
  availableLocations: Location[];
}

export default function GameProposalForm({ 
  onSubmit, 
  onClose, 
  initialData, 
  isEditing, 
  currentUser,
  availableLocations = []
}: GameProposalFormProps) {


  // Move all state declarations to the top
  const [formData, setFormData] = useState({
    sport: initialData?.sport || 'padel' as Sport,
    locations: initialData?.locations || [] as string[],
    date: initialData?.date ? toLocalISOString(new Date(initialData.date)) : '',
    startTime: formatTimeForSelect(initialData?.start_time || initialData?.startTime || ''),
    endTime: formatTimeForSelect(initialData?.end_time || initialData?.endTime || ''),
    description: initialData?.description || '',
    requiredCategories: initialData?.requiredCategories || [] as (PadelCategory | BeachTennisCategory | TennisCategory)[],
    gender: initialData?.gender || 'male' as GameGender,
    maxPlayers: initialData?.maxPlayers || 4,
    is_organizer_only: initialData?.is_organizer_only || false, // Add this line
    extra_cost: initialData?.extra_cost || '',
  });

  const [isManual, setIsManual] = useState(false);

  // Add these state declarations before any function that uses them
  const [isPublic, setIsPublic] = useState(true);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [userGroups, setUserGroups] = useState<Group[]>([]);
  const [searchGroupTerm, setSearchGroupTerm] = useState('');
  const [searchGroupResults, setSearchGroupResults] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);
  const [groupRequestStatus, setGroupRequestStatus] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState('');
  const timeOptions = generateTimeOptions();

  // State for creating a new group
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [newGroupData, setNewGroupData] = useState({
    name: '',
    avatar: undefined as string | undefined,
    isPublic: false,
    city: '',
    state: ''
  });
  const [createGroupError, setCreateGroupError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Add this handler
  const handleMaxPlayersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow clearing the input but set to minimum 2 players
    if (value === '') {
      setFormData({ ...formData, maxPlayers: formData.sport === 'tennis' ? 2 : 4 });
      return;
    }

    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 2) {
      setFormData({ ...formData, maxPlayers: numValue });
    }
  };

  // Add useEffect to fetch game groups when editing
  useEffect(() => {
    const fetchGameGroups = async () => {
      if (isEditing && initialData?.id) {
        try {
          // Fetch game groups
          const { data: gameGroups, error: groupsError } = await supabase
            .from('game_groups')
            .select(`
              group_id,
              groups (
                id,
                name,
                avatar,
                is_public,
                city,
                state
              )
            `)
            .eq('game_id', initialData.id);

          if (groupsError) throw groupsError;

          if (gameGroups && gameGroups.length > 0) {
            const formattedGroups = gameGroups
              .filter(gg => gg.groups) // Filter out null groups
              .map(gg => ({
                id: gg.groups?.id,
                group_id: gg.groups?.id || gg.group_id,
                name: gg.groups?.name || 'Unknown Group',
                avatar: gg.groups?.avatar,
                is_public: gg.groups?.is_public || false,
                city: gg.groups?.city,
                state: gg.groups?.state,
                role: 'member' // Default role
              }));
            
            setSelectedGroups(formattedGroups);
            setIsPublic(false); // If there are groups, the game is private
          } else {
            setIsPublic(initialData.is_public ?? true);
          }
        } catch (error) {
          console.error('Error fetching game groups:', error);
          // Fallback to public if there's an error
          setIsPublic(initialData?.is_public ?? true);
        }
      }
    };

    fetchGameGroups();
  }, [isEditing, initialData]);

  useEffect(() => {
    if (initialData && isEditing) {
      setFormData(prev => ({
        ...prev,
        startTime: formatTimeForSelect(initialData.start_time || initialData.startTime || prev.startTime),
        endTime: formatTimeForSelect(initialData.end_time || initialData.endTime || prev.endTime)
      }));
    }
  }, [initialData, isEditing]);

  const handleLocationToggle = (locationId: string) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.includes(locationId)
        ? prev.locations.filter(id => id !== locationId)
        : [...prev.locations, locationId]
    }));
    setError('');
  };

  const handleCategoryToggle = (category: PadelCategory | BeachTennisCategory | TennisCategory) => {
    setFormData(prev => ({
      ...prev,
      requiredCategories: prev.requiredCategories.includes(category)
        ? prev.requiredCategories.filter(c => c !== category)
        : [...prev.requiredCategories, category]
    }));
    setError('');
  };

  const handleSportChange = (sport: Sport) => {
    setFormData(prev => ({
      ...prev,
      sport,
      requiredCategories: [],
      maxPlayers: sport === 'tennis' ? 2 : 4
    }));
    setError('');
  };


  const handleGroupModalOpen = () => {
    if (!currentUser) {
      setIsPublic(true);
      return;
    }
  
    setIsPublic(false);
    setShowGroupModal(true);
    fetchUserGroups();
  };
  
  const handleCloseGroupModal = () => {
    if (selectedGroups.length === 0) {
      setIsPublic(true);
    }
    setShowGroupModal(false);
  };
  
  const fetchUserGroups = async () => {
    if (!currentUser) return;
    
    try {
      const { data: groups, error } = await supabase
        .from('group_members')
        .select(`
          group_id,
          role,
          groups (
            id,
            name,
            avatar,
            is_public,
            city,
            state
          )
        `)
        .eq('user_id', currentUser.id);
  
      if (error) throw error;
  
      if (groups) {
        const formattedGroups = groups
          .filter(g => g.groups) // Filter out null groups
          .map(g => ({
            id: g.groups?.id,
            group_id: g.group_id || g.groups?.id,
            name: g.groups?.name || 'Unknown Group',
            avatar: g.groups?.avatar,
            is_public: g.groups?.is_public || false,
            role: g.role || 'member',
            city: g.groups?.city,
            state: g.groups?.state
          }));
        setUserGroups(formattedGroups);
      }
    } catch (error) {
      console.error('Error fetching user groups:', error);
      setUserGroups([]); // Set empty array on error
    }
  };
  
  const handleJoinGroup = async (group: any) => {
    try {
      if (group.is_public) {
        const formattedGroup = {
          group_id: group.id,
          name: group.name,
          avatar: group.avatar,
          is_public: group.is_public,
          role: 'member'
        };
        
        if (currentUser) {
          await supabase
            .from('group_members')
            .insert({
              user_id: currentUser.id,
              group_id: group.id,
              role: 'member'
            });
          
          await fetchUserGroups();
          handleGroupToggle(formattedGroup);
          setIsPublic(false);
        }
      } else {
        const { error } = await supabase
          .from('group_members')
          .insert({
            user_id: currentUser?.id,
            group_id: group.id,
            role: 'pending'
          });
        
        if (error) throw error;
        
        setGroupRequestStatus(prev => ({ ...prev, [group.id]: true }));
        alert('Uma solicitação de aprovação foi enviada para o administrador do grupo.');
      }
    } catch (error) {
      console.error('Error joining group:', error);
      alert('Erro ao solicitar entrada no grupo. Tente novamente.');
    }
  };
  
  const handleSearchGroups = async (term: string) => {
    setSearchGroupTerm(term);
    if (term.length < 3) {
      setSearchGroupResults([]);
      return;
    }
  
    try {
      const { data, error } = await supabase
        .from('groups')
        .select('id, name, is_public, avatar, city, state')
        .ilike('name', `%${term}%`)
        .limit(5);
  
      if (error) throw error;
      setSearchGroupResults(data || []);
    } catch (error) {
      console.error('Error searching groups:', error);
    }
  };
  
  const handleGroupToggle = (group: Group) => {
    setSelectedGroups(prev => 
      prev.some(g => g.group_id === group.group_id)
        ? prev.filter(g => g.group_id !== group.group_id)
        : [...prev, group]
    );
  };

  const handleCreateGroup = async () => {
    if (!newGroupData.name.trim()) {
      setCreateGroupError('O nome do grupo é obrigatório.');
      return;
    }
    if (!newGroupData.city.trim() || !newGroupData.state.trim()) {
      setCreateGroupError('Cidade e estado são obrigatórios.');
      return;
    }

    try {
      const { data: group, error: groupError } = await supabase
        .from('groups')
        .insert([{
          name: newGroupData.name,
          avatar: newGroupData.avatar,
          created_by: currentUser.id,
          is_public: newGroupData.isPublic,
          city: newGroupData.city,
          state: newGroupData.state
        }])
        .select()
        .single();

      if (groupError) throw groupError;

      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: group.id,
          user_id: currentUser.id,
          role: 'admin'
        });

      if (memberError) throw memberError;

      // After creating, fetch user groups again and select the new one
      await fetchUserGroups();
      handleGroupToggle({ ...group, group_id: group.id, role: 'admin' });
      setShowCreateGroupModal(false);
      setNewGroupData({ name: '', avatar: undefined, isPublic: false, city: '', state: '' });

    } catch (error) {
      console.error('Error creating group:', error);
      setCreateGroupError('Erro ao criar grupo. Tente novamente.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return; // User canceled file selection
    }

    if (file.size > 3 * 1024 * 1024) {
      alert('A imagem deve ter no máximo 3MB');
      return;
    }

    try {
      const resizedImage = await resizeImage(file);
      setNewGroupData({ ...newGroupData, avatar: resizedImage });
    } catch (err) {
      alert('Erro ao processar a imagem');
    }
  };

  // Função para gerar opções de horário de término baseado no horário de início
  const getEndTimeOptions = () => {
    if (!formData.startTime) {
      return timeOptions;
    }
    
    const startIndex = timeOptions.indexOf(formData.startTime);
    
    if (startIndex === -1) {
      return timeOptions;
    }
    
    // Retorna apenas os horários que vêm depois do horário de início
    return timeOptions.slice(startIndex + 1);
  };

  const validateForm = () => {
    if (formData.locations.length === 0) {
      setError('Selecione pelo menos uma localização');
      return false;
    }
    if (formData.requiredCategories.length === 0) {
      setError('Selecione pelo menos uma categoria');
      return false;
    }
    if (formData.maxPlayers < 2) {
      setError('O número de jogadores não pode ser menor que 2');
      return false;
    }
    if (!validateTimes(formData.startTime, formData.endTime)) {
      setError('O horário de término deve ser depois do horário de início');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
      
    if (!validateForm()) {
      return;
    }

    try {
      const normalizedDate = normalizeDate(formData.date);
      
      // Prepare the data to send to the parent component
      const submissionData = {
        ...formData,
        date: normalizedDate,
        is_public: isPublic,
        groups: selectedGroups.map(group => group.group_id || group.id).filter(Boolean)
      };

      // Call the onSubmit callback with the form data
      onSubmit(submissionData);
      
    } catch (error) {
      console.error('Error in form submission:', error);
      setError('Erro ao processar o formulário. Tente novamente.');
    }
  };

  useEffect(() => {
    if (!isPublic && selectedGroups.length === 0) {
      setIsPublic(true);
    }
  }, [selectedGroups, isPublic]);

  // Add useEffect to handle currentUser changes
  useEffect(() => {
    if (!currentUser) {
      setIsPublic(true);
      setSelectedGroups([]);
      setUserGroups([]);
    }
  }, [currentUser]);
  

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Esporte
        </label>
        <div className="flex flex-wrap gap-2">
          {(['padel', 'beach-tennis', 'tennis'] as Sport[]).map((sportOption) => (
            <button
              key={sportOption}
              type="button"
              onClick={() => handleSportChange(sportOption)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                formData.sport === sportOption
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {sportOption === 'padel' ? 'Padel' : sportOption === 'beach-tennis' ? 'Beach Tennis' : 'Tênis'}
            </button>
          ))}
        </div>
      </div>

      <div>
  <div className="flex items-center gap-2 mb-2">
    <label className="block text-sm font-medium text-gray-700">
      Esse jogo vai ser visível por:
    </label>
    <VisibilityTooltip />
  </div>
  <div className="flex gap-4">
    <label className="flex items-center cursor-pointer">
      <input
        type="radio"
        name="visibility"
        checked={isPublic}
        onChange={() => {
          setIsPublic(true);
          setShowGroupModal(false);
        }}
        className="h-4 w-4 rounded-full border-gray-300 text-blue-600 focus:blue-500"
      />
      <span className="text-sm text-gray-700 ml-2">Todos</span>
    </label>
    <label 
      className="flex items-center cursor-pointer"
      onClick={handleGroupModalOpen}
    >
      <input
        type="radio"
        name="visibility"
        checked={!isPublic}
        onChange={() => {}}
        className="h-4 w-4 rounded-full border-gray-300 text-blue-600 focus:blue-500"
      />
      <span className="text-sm text-gray-700 ml-2">
        Grupo(s) {selectedGroups.length > 0 && `(${selectedGroups.length})`}
      </span>
    </label>
  </div>
</div>



      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tipo de Jogo
        </label>
        <div className="flex flex-wrap gap-2">
          {(['mixed', 'male', 'female'] as GameGender[]).map((genderOption) => (
            <button
              key={genderOption}
              type="button"
              onClick={() => setFormData({ ...formData, gender: genderOption })}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                formData.gender === genderOption
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {genderOption === 'mixed' ? 'Misto' : genderOption === 'male' ? 'Masculino' : 'Feminino'}
            </button>
          ))}
        </div>
      </div>

      {/* Add this new section for max players */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Número de Jogadores
        </label>
        <div className="flex flex-wrap gap-2 items-center">
          {formData.sport === 'tennis' ? (
            <>
              {[2, 4].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, maxPlayers: num });
                    setIsManual(false);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.maxPlayers === num && !isManual
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {num} jogadores
                </button>
              ))}
            </>
          ) : (
            <>
              {[4].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, maxPlayers: num });
                    setIsManual(false);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.maxPlayers === num && !isManual
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {num} jogadores
                </button>
              ))}
            </>
          )}
          <button
            type="button"
            onClick={() => setIsManual(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isManual
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Outro
          </button>
          {isManual && (
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={formData.maxPlayers}
              onChange={handleMaxPlayersChange}
              className="w-24 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              min="1"
            />
          )}
        </div>
      </div>

      
      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Locais (selecione um ou mais)
          </label>
          <LocationInfoTooltip locations={availableLocations} />
        </div>
        <div className="relative">
          <div className="overflow-x-auto pb-2 hide-scrollbar">
            <div className="flex gap-2 min-w-min">
              {availableLocations.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  onClick={() => handleLocationToggle(location.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    formData.locations.includes(location.id)
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {location.name}
                </button>
              ))}
            </div>
          </div>
          <div className="pointer-events-none absolute right-0 top-0 h-full w-20 bg-gradient-to-l from-white to-transparent" />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Categorias Permitidas (selecione uma ou mais)
          </label>
          <CategoryTooltip />
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.sport === 'padel' && padelCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => handleCategoryToggle(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                formData.requiredCategories.includes(category)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
          {formData.sport === 'beach-tennis' && beachTennisCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => handleCategoryToggle(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                formData.requiredCategories.includes(category)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
          {formData.sport === 'tennis' && tennisCategories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => handleCategoryToggle(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                formData.requiredCategories.includes(category)
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Organizer Only Checkbox */}
      <div className="flex items-center">
        <input
          id="organizer-only"
          type="checkbox"
          checked={formData.is_organizer_only}
          onChange={(e) => setFormData({ ...formData, is_organizer_only: e.target.checked })}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="organizer-only" className="ml-2 block text-sm text-gray-900">
          Não vou jogar, apenas organizar
        </label>
      </div>

      <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data e Horário
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="relative flex-1">
                  <input
                    type="date"
                    required
                    min={toLocalISOString(new Date())}
                    className="w-full pl-4 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors hover:bg-gray-100 text-sm appearance-none box-border min-h-[42px] h-[42px]"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                  {!formData.date && (
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400 text-sm">
                      Data
                    </div>
                  )}
                </div>
    
                <div className="relative flex-1">
                  <select
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none transition-colors hover:bg-gray-100 text-sm box-border min-h-[42px] h-[42px]"
                    value={formData.startTime}
                    onChange={(e) => {
                      const newTime = e.target.value;
                      setFormData(prev => {
                        // Limpar o horário de término se ele for anterior ou igual ao novo horário de início
                        const newEndTime = prev.endTime && newTime && prev.endTime <= newTime ? '' : prev.endTime;
                        return {
                          ...prev,
                          startTime: newTime,
                          endTime: newEndTime
                        };
                      });
                      setError('');
                    }}
                  >
                    <option value="">Início</option>
                    {timeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
    
                <div className="relative flex-1">
                  <select
                    required
                    className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none transition-colors hover:bg-gray-100 text-sm box-border min-h-[42px] h-[42px]"
                    value={formData.endTime}
                    onChange={(e) => {
                      setFormData({ ...formData, endTime: e.target.value });
                      setError('');
                    }}
                    disabled={!formData.startTime}
                  >
                    <option value="">Término</option>
                    {getEndTimeOptions().map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Informações Adicionais
          </label>
          <textarea
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={4}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="(OPCIONAL)Adicione informações importantes sobre o jogo..."
          />
        </div>
      </div>

      <div>
        <div className="flex items-center gap-2 mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Custos Adicionais
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={formData.extra_cost}
            onChange={(e) => setFormData({ ...formData, extra_cost: e.target.value })}
            placeholder="(OPCIONAL) Ex: R$10 para quadra"
          />
        </div>
      </div>

      {showGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Selecionar Grupos</h3>
              <button onClick={handleCloseGroupModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="relative">
              <input
                type="text"
                value={searchGroupTerm}
                onChange={(e) => handleSearchGroups(e.target.value)}
                placeholder="Pesquisar ou criar grupos..."
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            </div>

            <div className="max-h-60 overflow-y-auto space-y-3 p-1 -m-1">
              {searchGroupResults.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700 px-2">Resultados da Pesquisa</h4>
                  {searchGroupResults.map((group) => (
                    <div key={group.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        <img src={group.avatar || '/default-group.png'} alt={group.name} className="w-10 h-10 rounded-full object-cover" />
                        <div>
                          <span className="font-medium">{group.name}</span>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span>{group.is_public ? 'Público' : 'Privado'}</span>
                            {group.city && <span>• {group.city}</span>}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleJoinGroup(group)}
                        className={`text-blue-600 hover:text-blue-800 text-sm font-medium ${groupRequestStatus[group.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
                        disabled={groupRequestStatus[group.id]}
                      >
                        {group.is_public ? 'Entrar' : groupRequestStatus[group.id] ? 'Pendente' : 'Solicitar'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700 px-2">Meus Grupos</h4>
                {userGroups.map((group) => (
                  <div key={group.group_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      <img src={group.avatar || '/default-group.png'} alt={group.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <span className="font-medium">{group.name}</span>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{group.is_public ? 'Público' : 'Privado'}</span>
                          {group.city && <span>• {group.city}</span>}
                        </div>
                        {group.role === 'pending' && <span className="text-xs text-yellow-600 block">Aguardando aprovação</span>}
                      </div>
                    </div>
                    {group.role !== 'pending' && (
                      <button
                        type="button"
                        onClick={() => handleGroupToggle(group)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium ${
                          selectedGroups.some(g => g.group_id === group.group_id)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {selectedGroups.some(g => g.group_id === group.group_id) ? 'Selecionado' : 'Selecionar'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-between items-center pt-4 border-t">
              <button
                type="button"
                onClick={() => setShowCreateGroupModal(true)}
                className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                <Plus className="w-4 h-4" />
                Criar Novo Grupo
              </button>
              <button
                type="button"
                onClick={handleCloseGroupModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Criar Novo Grupo</h3>
              <button onClick={() => setShowCreateGroupModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            {createGroupError && <p className="text-red-500 text-sm">{createGroupError}</p>}

            <div className="flex items-center gap-4">
              {newGroupData.avatar ? (
                <img src={newGroupData.avatar} alt="Avatar do grupo" className="w-16 h-16 rounded-full object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg">
                Alterar Foto
              </button>
            </div>

            <input
              type="text"
              value={newGroupData.name}
              onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
              placeholder="Nome do Grupo"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={newGroupData.city}
                onChange={(e) => setNewGroupData({ ...newGroupData, city: e.target.value })}
                placeholder="Cidade"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="text"
                value={newGroupData.state}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  if (value.length <= 2 && /^[A-Z]*$/.test(value)) {
                    setNewGroupData({ ...newGroupData, state: value });
                  }
                }}
                placeholder="UF"
                maxLength={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg uppercase"
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={newGroupData.isPublic}
                onChange={(e) => setNewGroupData({ ...newGroupData, isPublic: e.target.checked })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-900">
                Grupo Público (qualquer um pode entrar)
              </label>
            </div>

            <div className="flex justify-end gap-4">
              <button type="button" onClick={() => setShowCreateGroupModal(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg">
                Cancelar
              </button>
              <button type="button" onClick={handleCreateGroup} className="px-4 py-2 text-white bg-blue-600 rounded-lg">
                Criar e Selecionar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 hover:text-gray-900"
        >
          Cancelar
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
        >
          {isEditing ? 'Salvar Alterações' : 'Propor Jogo'}
        </button>
      </div>
    </form>
  );
}